import firebase from 'firebase'
import * as Sentry from '@sentry/vue'
import { authApp } from '~/services/firebase'
import { mapGetters, mapMutations } from 'vuex'
import queries from '~/mixins/queries'
import { signInAnonymously } from '~/services/auth'
import { logoutExt } from '~/logicJs/LogoutUtils'

export default {
  mixins: [queries],
  methods: {
    ...mapMutations([
      'SET_CURRENT_USER',
      'SET_POPUP_EMAIL_SHOWN',
      'SET_TOKEN',
      'SET_POPUP_EMAIL_SHOWN',
      'SET_MAIN_LOADER',
    ]),
    async login(user) {
      if (user) {
        this.SET_CURRENT_USER(_.cloneDeep(user))
      }
      console.log(
        'Auth state changed this.$route.query:',
        user,
        this.$route.query
      )
      const isEmailLink = firebase
        .auth(authApp)
        .isSignInWithEmailLink(window.location.href)
      // A real user has a uid — a stale/empty store object must not be treated
      // as "already authenticated" (that path strips the one-time oobCode).
      const hasUser = !!user?.uid

      let userCreds
      try {
        if (isEmailLink && hasUser && !user.isAnonymous) {
          console.log('initializeAuth: already authenticated, skipping email link')
          // Strip query params (apiKey, oobCode…) so a page refresh won't retry with a spent code
          window.history.replaceState(null, '', window.location.pathname)
          userCreds = firebase.auth(authApp).currentUser
        } else if (isEmailLink) {
          console.log('initializeAuth: isEmailLink && hasUser && isAnonymous')
          let email = window.localStorage.getItem('emailForSignIn')
          if (!email) {
            this.$notify({
              title: 'Email required',
              message: 'Please enter your email address to complete sign-in.',
              type: 'info',
              duration: 5000,
            })
            this.SET_POPUP_EMAIL_SHOWN(true)
            console.log('SET_MAIN_LOADER !email')
            return
          } else {
            userCreds = await this.handleEmailLinkSignIn(email)
            if (userCreds) {
              console.log('initializeAuth: userCreds', userCreds)
            } else {
              console.error('Error signing in with email link')
              userCreds = await signInAnonymously()
            }
          }
        } else {
          if (hasUser) {
            console.log('initializeAuth: hasUser')
            userCreds = firebase.auth(authApp).currentUser
          } else {
            console.log('initializeAuth: else')
            userCreds = await signInAnonymously()
          }
        }

        if (userCreds) {
          const user = firebase.auth(authApp).currentUser
          console.log('initializeAuth: userCreds - user', userCreds, user)

          let currentToken
          try {
            currentToken = await user.getIdToken(true)
          } catch (refreshError) {
            // The forced refresh needs securetoken.googleapis.com, which corporate
            // proxies may block. Right after a sign-in the cached token is fresh —
            // use it instead of leaving the store half-updated (user committed at
            // the top of login(), token never committed => UI logged in while axios
            // keeps an anonymous/stale token => "N/A" access requests, Asana 1215474368844464).
            console.error(
              'initializeAuth: getIdToken(true) failed, falling back to cached token',
              refreshError
            )
            try {
              currentToken = await user.getIdToken(false)
            } catch (tokenError) {
              // No usable token at all: put the store back into a consistent
              // logged-out state instead of an authenticated UI that cannot call
              // the API, then let the outer catch own the UX (notify + Sentry).
              console.error(
                'initializeAuth: no usable token, resetting auth state',
                tokenError
              )
              this.SET_CURRENT_USER(null)
              this.SET_TOKEN('')
              throw tokenError
            }
          }
          this.SET_TOKEN(currentToken)
          this.SET_CURRENT_USER(_.cloneDeep(user))
        } else {
          console.error('Error signing in')
          const query = { ...this.$route.query, error: 'Error signing in' }
          this.$router.push({ path: '/', query })
          console.log('SET_MAIN_LOADER Error signing in')
          this.SET_MAIN_LOADER(false) // Hide loader
          //todo: display error/redirect to error page
        }
      } catch (error) {
        // Sign-in threw (e.g. auth/too-many-requests when Firebase abuse
        // protection blocks the device — Sentry TRUSTPAGE-C). Handle the UX
        // here (loader + notification), keep Sentry visibility for codes we
        // don't deliberately filter, then re-throw so programmatic callers
        // (ReqDocs.vue sendLoginLink) keep their error semantics — otherwise
        // a failed sign-in would stamp the 30-min email-link throttle and
        // close the email dialog as if it had succeeded.
        console.error('login: sign-in failed', error, error?.code)
        Sentry.captureException(error) // beforeSend drops the filtered codes
        this.$notify({
          title: 'Sign-in error',
          message:
            error?.code === 'auth/too-many-requests'
              ? 'Access from this device is temporarily blocked due to unusual activity. Please try again later.'
              : 'An error occurred during sign-in. Please try again.',
          type: 'error',
          duration: 6000,
        })
        console.log('SET_MAIN_LOADER sign-in failed')
        this.SET_MAIN_LOADER(false) // Hide loader
        throw error
      }
      console.log('SET_MAIN_LOADER false after login')
      this.SET_POPUP_EMAIL_SHOWN(false) // Close the dialog
    },
    async logout() {
      // Clear local storage + signout from firebase + redirect to /
      this.auditLogCreate({
        action: 'logout',
      })
      logoutExt(this.SET_CURRENT_USER, this.SET_TOKEN)
    },
    /**
     * Handles email link authentication workflow.
     * This method checks if the current URL contains an email sign-in link and completes
     * the sign-in process by either linking the credentials to an existing anonymous user
     * or signing in with the provided credentials.
     *
     * @return {Promise<void>} - A promise that resolves when the email link sign-in process is completed.
     */
    //async handleEmailLink() {
    //  console.log('handleEmailLink', window.location.href);
    //  if (firebase.auth(authApp).isSignInWithEmailLink(window.location.href)) {
    //    console.log('isSignInWithEmailLink', window.location.href);
    //    const uid = firebase.auth(authApp).currentUser.uid;
    //    let user = _.cloneDeep(firebase.auth(authApp).currentUser)
    //    this.SET_CURRENT_USER(user);
    //    let email = window.localStorage.getItem('emailForSignIn');
    //
    //    if(!email) {
    //      //open popup signup
    //      console.log('open popup signup')
    //      this.SET_POPUP_EMAIL_SHOWN(true)
    //      return
    //    }
    //    try {
    //      this.SET_POPUP_EMAIL_SHOWN(false)
    //      console.log('emailForSignIn', email);
    //      const credential = firebase.auth.EmailAuthProvider.credentialWithLink(email, window.location.href);
    //
    //      if (user && user.isAnonymous) {
    //
    //        console.log('Upgrading anonymous account', uid);
    //        await user.linkWithCredential(credential);
    //        console.log('Anonymous account successfully upgraded', user);
    //        this.SET_CURRENT_USER(user);
    //      } else {
    //        await firebase.auth(authApp).signInWithCredential(credential);
    //      }
    //
    //      window.localStorage.removeItem('emailForSignIn');
    //    } catch (error) {
    //      console.error('Error upgrading anonymous account', error, error.code);
    //      if(email) {
    //       // refresh link
    //       this.$axios.post(`/trust-center/email-link`, {
    //         "email": email,
    //         "refreshOnly": true
    //       }, {headers: {
    //          'x-tc-domain-local': window.location.origin
    //         }});
    //      }
    //    }
    //  }
    //},
    async refreshEmailLink(email) {
      try {
        const linkResponse = await this.$axios.post(
          `/trust-center/email-link`,
          {
            email: email,
          },
          {
            headers: {
              // 'x-tc-domain-local': window.location.origin //todo: fix for localhost
            },
          }
        )
        // message A new email verification email has been sent to ${email}
        this.$notify({
          message: `A new email verification email has been sent to ${email}`,
          type: 'success',
        })

        console.log('refreshEmailLink: response', linkResponse)
        if (linkResponse.data && linkResponse.data.link) {
          return linkResponse.data.link
        }
      } catch (error) {
        console.error('refreshEmailLink: Error refreshing email link', error)
      }
      return null
    },
    async handleEmailLinkSignIn(email) {
      if (!email) {
        console.error('joinEmailCredentials: No email provided')
        return null
      }
      console.log('handleEmailLink', window.location.href)
      if (firebase.auth(authApp).isSignInWithEmailLink(window.location.href)) {
        console.log(
          'joinEmailCredentials: isSignInWithEmailLink',
          window.location.href
        )
        const currentUser = firebase.auth(authApp).currentUser

        try {
          let userCreds = null
          const credential = firebase.auth.EmailAuthProvider.credentialWithLink(
            email,
            window.location.href
          )
          // Strip query params (apiKey, oobCode…) so a page refresh won't retry with a spent code
          window.history.replaceState(null, '', window.location.pathname)
          if (currentUser && currentUser.isAnonymous) {
            try {
              console.log(
                'joinEmailCredentials: Upgrading anonymous account',
                currentUser.uid
              )
              userCreds = await currentUser.linkWithCredential(credential)
            } catch (error) {
              console.error(
                'Error linking anonymous account',
                error,
                error.code
              )
              userCreds = await firebase
                .auth(authApp)
                .signInWithCredential(credential)
            }
          } else {
            userCreds = await firebase
              .auth(authApp)
              .signInWithCredential(credential)
          }
          return userCreds
        } catch (error) {
          console.error('Error logging in with email link', error, error.code)

          const isExpiredOrInvalid = [
            'auth/invalid-action-code',
            'auth/expired-action-code',
          ].includes(error.code)

          // Strip query params (apiKey, oobCode…) so a page refresh won't retry with an expired code
          window.history.replaceState(null, '', window.location.pathname)
          await signInAnonymously()

          if (isExpiredOrInvalid) {
            this.$notify({
              title: 'Link expired',
              message: `Your sign-in link has expired. A new link is being sent to ${email}.`,
              type: 'warning',
              duration: 6000,
            })
            const newLink = await this.refreshEmailLink(email)
            console.log('joinEmailCredentials: newLink', newLink)
            if (newLink) {
              setTimeout(() => window.location.replace(newLink), 4000)
            }
          } else {
            this.$notify({
              title: 'Sign-in error',
              message: 'An error occurred during sign-in. Please try again.',
              type: 'error',
              duration: 5000,
            })
          }

          return null
        }
      }
    },
    async sendSignInLink(email) {
      // The email-link endpoint needs an authenticated (anonymous) session:
      // without one the axios interceptor sends no Authorization header and
      // the backend rejects the POST. The session can be missing when the
      // visitor's network blocks Firebase (corporate proxies) or the device
      // is blocked by abuse protection (auth/too-many-requests, PR #34) —
      // previously this crashed on `currentUser.uid` below and showed the
      // misleading generic "try again" message. Recover the session first.
      if (!firebase.auth(authApp).currentUser) {
        try {
          console.log('sendSignInLink: no auth session, signing in anonymously')
          const cred = await signInAnonymously()
          // Commit the fresh cached token (no forced refresh): with an empty
          // store token the axios interceptor would force a
          // securetoken.googleapis.com round-trip, and that endpoint is
          // blocked on the very corporate networks this recovery targets
          // (same failure mode PR #36 addresses in login()).
          this.SET_TOKEN(await cred.user.getIdToken())
        } catch (signInError) {
          console.error(
            'sendSignInLink: could not establish a session',
            signInError,
            signInError?.code
          )
          // captureMessage, not captureException: too-many-requests and
          // network-request-failed exceptions are dropped by the global
          // filters (sentry.client.js), but on this flow a visitor is
          // actively trying to sign in — we want to see it.
          // code as a tag (not extra) so Sentry alert rules can filter the
          // bot-driven too-many-requests volume without losing the event.
          Sentry.captureMessage('sendSignInLink: could not establish a session', {
            level: 'error',
            tags: { flow: 'email-link', code: signInError?.code ?? 'unknown' },
          })
          this.errorMessage =
            signInError?.code === 'auth/too-many-requests'
              ? 'Access from this device is temporarily blocked due to unusual activity. Please try again later.'
              : 'Unable to reach the sign-in service — your network may be blocking it. Please try again later or from another network.'
          this.loading = false
          throw { message: this.errorMessage }
        }
      }
      try {
        console.log('CURRENT USER', firebase.auth(authApp).currentUser?.uid)
        let response = await this.$axios.post(
          '/trust-center/email-link',
          { email: email },
          {
            headers: {
              // 'x-tc-domain-local': window.location.origin
            },
          }
        )
        console.log('response', response)

        window.localStorage.setItem('emailForSignIn', email)
        this.$emit('close')
        this.loading = false
        //alert('Check your email for the sign-in link!');
      } catch (error) {
        console.error(
          'Error sending sign-in link to email',
          error.message,
          error.response,
          error?.response?.data?.message
        )
        // This failure was console-only before (0 Sentry events in 90 days
        // despite user reports) — capture it, tagged by transport outcome.
        Sentry.captureException(error, {
          tags: {
            flow: 'email-link',
            httpStatus: error?.response?.status ?? 'network',
          },
        })
        let message = error?.response?.data?.message
        this.errorMessage = message
          ? message
          : error?.response
          ? 'Error sending sign-in link. Please try again.'
          : 'Unable to reach the server — your network may be blocking the request. Please try again later or from another network.'
        this.loading = false
        throw {message: this.errorMessage}
      }
    },
  },
  computed: {
    ...mapGetters(['GET_CURRENT_USER']),
  },
}
